import React from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import { 
  DatePicker as KendoDatePicker, 
  DatePickerChangeEvent
} from '@progress/kendo-react-dateinputs';
import _ from 'lodash';
import { Keys } from '@progress/kendo-react-common';
import { Label } from '@progress/kendo-react-labels';
import classNames from 'classnames';
import { 
  ABSTRACT_FIELD_OMITTED_PROPS, 
  FIELD_OMITTED_PROPS, 
  getUnixDate
} from '../../../../utility/Util';
import { PUZZLE_FIELD_STATE } from '../Types';
import {
  PUZZLE_DATE_PICKER_ON_BLUR_EVENT,
  PUZZLE_DATE_PICKER_PROPS
} from './Types';
import Field from '../../../layout/field/Field';

/**
 * For the layout and design purposes the entire Kendo Datepicker needs to be wrapped with this cmp.
 * Cmp handles all events that occur and flags component state represented with CSS classes
 * @author Stevan Stojanovic
 */
class PuzzleDatePicker extends PureComponent<PUZZLE_DATE_PICKER_PROPS, PUZZLE_FIELD_STATE> {
  datePickerRef: any;
  datePickerID: any = null;

  static defaultProps = {
    size: 'medium'
  }

  /**
   * Adding aria-required attribute to input - accessibility
   */
  componentDidMount() {
    const { required, id, disabled } = this.props;
    this.datePickerID = id ? id : this.datePickerRef?._dateInput?.current?._element?.id;
    if (required) { 
      this.datePickerRef.dateInput.element.setAttribute('aria-required', 'true');
    }
    this.setToggleButtonTabIndex(disabled);
  }

  componentDidUpdate(prevProps: PUZZLE_DATE_PICKER_PROPS) {
    if (prevProps.disabled !== this.props.disabled) {
      this.setToggleButtonTabIndex(this.props.disabled);
    }
  }

  componentWillUnmount(): void {
    if (this.datePickerRef?.calendar) {
      this.datePickerRef.calendar.element?.removeEventListener('keydown', this.calendarOnKeyDown);
    }
  }

  /**
   * Sets the tabIndex for the calendar toggle button based on the disabled state.
   *
   * @param {boolean} disabled - Indicates whether the date picker is disabled.
   */
  setToggleButtonTabIndex = (disabled: boolean) => {
    const inputParent = this.datePickerRef?.dateInput?.element?.parentElement;
    if (inputParent) {
      const button = inputParent.nextElementSibling;
      if (button) {
        button.tabIndex = disabled ? -1 : 0;
      }
    }
  }

  /**
   * Focuses input on calendar esc or tab keydown
   *
   * @param e
   */
  calendarOnKeyDown = (e: any) => {
    if ([Keys.esc, Keys.tab].includes(e.keyCode)) {
      this.datePickerRef.dateInput.element.focus();
    }
  };

  /**
   * Set the month value depending on the format
   * @param {string | undefined} format
   */
  setMonthValue = (format:string | undefined) => {
    let returnValue = '1';
    if (format) {
      if (format.includes('MMMM')) {
        returnValue = 'January';
      } else if (format.includes('MMM')) {
        returnValue = 'Jan';
      } else if (format.includes('MM')) {
        returnValue = '01';
      } else if (format.includes('M')) {
        returnValue = '1';
      }
    }
    return returnValue;
  }

  /**
   * Set the year value depending on the format
   * @param {string | undefined} format
   */
  setYearValue = (format:string | undefined) => {
    if (format?.includes('yy') && !format?.includes('yyy')) {
      return '70';
    } else {
      return '1970';
    }
  }

  handleBlur = (event: PUZZLE_DATE_PICKER_ON_BLUR_EVENT) => {
    let { name, value } = event.target;
    if (!_.isDate(value) && _.isString(value) && (value.includes('month') || value.includes('day') || value.includes('year'))) {
      const { format } = this.props;
      const day: string = format?.includes('dd') ? '01' : '1';
      const month: string = this.setMonthValue(format);
      const year: string = this.setYearValue(format);
      value = value.replace('month', month).replace('day', day).replace('year', year);
    }

    if (this.props.onBlur) {
      this.props.onBlur({ value, name });
    }
  }

  /**
   * DatePicker change handler that intercepts mouse clicked events (when user clicks on a date within calendar)
   * and sets time to 23:59 if the time is 00:00
   * @param event
   */
  handleDateChange = (event: DatePickerChangeEvent) => {
    const isMouseEvent = event.nativeEvent instanceof MouseEvent;
    const isMouseSyntheticEvent = event.syntheticEvent instanceof MouseEvent;
    if (isMouseEvent || isMouseSyntheticEvent) {
      const { value } = event.target;
      if (value && value.getHours() === 0 && value.getMinutes() === 0) {
        event.target.value?.setHours(23, 59);
        // @ts-ignore
        event.target.valueDuringOnChange?.setHours(23, 59);
      }
    }
    this.props.onChange(event);
  };

  /**
   * Accessibility - adding aria-label
   * Adding event listener on calendar
   */
  datePickerOnFocus = (e: any) => {
    if (e.target.classList.contains('k-input-inner')) {
      let ariaLabel = this.props.label;
      if (this.props.ariaLabelLocale) {
        ariaLabel = this.props.ariaLabelLocale;
      }
      e.target.setAttribute('aria-label', `${ariaLabel}`);
    }
    if (this.datePickerRef?.calendar) {
      this.datePickerRef.calendar.element?.addEventListener('keydown', this.calendarOnKeyDown);
    }
  };

  /**
   * Validate date field
   */
  validateField = (min: Date | undefined, value: Date | null | undefined, valid: boolean | undefined) => {
    let unixMin;
    let unixValue;

    if (min) {
      unixMin = getUnixDate(min);
    }
    if (value) {
      unixValue = getUnixDate(value);
    }

    return unixMin && unixValue ? (unixMin <= unixValue) : valid;
  }

  render() {
    const datepickerProps = { ...this.props };
    const datePickerOmittedProps = ['required'].concat(ABSTRACT_FIELD_OMITTED_PROPS, FIELD_OMITTED_PROPS);
    let defaultDatePickerProps: any = _.omit(datepickerProps, datePickerOmittedProps);
    let { value } = defaultDatePickerProps;
    if (!_.isDate(value) && _.isString(value)) {
      value = new Date(value);
      defaultDatePickerProps = { ...defaultDatePickerProps, value };
    }
    const isValid = this.validateField(defaultDatePickerProps.min, defaultDatePickerProps.value, defaultDatePickerProps.valid);
    const {
      asField,
      ariaDescribedBy,
      errorTxt,
      disabled,
      className = '',
      size,
      labelClassName,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      dataCy
    } = this.props;

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const fieldCls = classNames(
      {
        [className]: className,
        'k-medium': size === 'medium',
        'k-small': size === 'small',
        'k-state-empty': !value,
        'k-state-invalid': !isValid,
        'k-state-disabled': disabled
      }
    );

    const content = (
      <div
        className={`k-d-flex-col ${fieldCls}`}
        data-cy={dataCy ? `${dataCy}-wrapper` : 'datepickerWrap'}
      >
        {datepickerProps.label && <Label
          editorId={this.datePickerID} 
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {datepickerProps.label}
        </Label>}
        <KendoDatePicker
          id={this.datePickerID}
          {...defaultDatePickerProps}
          placeholder=""
          onBlur={this.handleBlur}
          onChange={this.handleDateChange}
          onFocus={this.datePickerOnFocus}
          valid={isValid}
          ref={(el: any) => this.datePickerRef = el}
          popupSettings={{
            popupClass: 'data-preventrecorddeselect'
          }}
        />
        {withValidation && <div
          className={'tp-validation-error-message'}
          data-cy={dataCy ? `${dataCy}-error-message` : undefined}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }
}

export default PuzzleDatePicker;
